import React, { FC, useState } from 'react'
import {
  Box,
  Stack,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material'
import GetAppIcon from '@mui/icons-material/GetApp'
import * as XLSX from 'xlsx'
import { Prescription } from '../../../types/models/Presc'

// 番号アノテーションフィールドタイプ
export type AnnotationFieldType =
  | '保険者番号'
  | '記号番号'
  | '公費負担番号'
  | '公費受給番号'
  | '枝番'

// アノテーションボックスのデータ型
export type AnnotationBox = {
  field: AnnotationFieldType
  origin_point_height_ratio: number
  origin_point_width_ratio: number
  range_height_ratio: number
  range_width_ratio: number
}

interface NumberAnnotationPanelProps {
  annotations: AnnotationBox[]
  onAnnotationsChange: (annotations: AnnotationBox[]) => void
  selectedField: AnnotationFieldType
  onFieldChange: (field: AnnotationFieldType) => void
  prescription?: Prescription
  isKigouTwoColumn: boolean
  onIsKigouTwoColumnChange: (value: boolean) => void
  unifiedMedicalCode: string
  onUnifiedMedicalCodeChange: (value: string) => void
}

// フィールドごとの色定義
export const fieldColors: Record<AnnotationFieldType, string> = {
  保険者番号: 'rgba(255, 0, 0, 0.3)',
  記号番号: 'rgba(0, 255, 0, 0.3)',
  公費負担番号: 'rgba(0, 0, 255, 0.3)',
  公費受給番号: 'rgba(255, 255, 0, 0.3)',
  枝番: 'rgba(255, 0, 255, 0.3)',
}

export const NumberAnnotationPanel: FC<NumberAnnotationPanelProps> = ({
  annotations,
  onAnnotationsChange,
  selectedField,
  onFieldChange,
  prescription,
  isKigouTwoColumn,
  onIsKigouTwoColumnChange,
  unifiedMedicalCode,
  onUnifiedMedicalCodeChange,
}) => {
  const [codeError, setCodeError] = useState<string>('')

  // 統一医療機関コードの検証
  const validateUnifiedMedicalCode = (code: string): boolean => {
    const regex = /^[0-9]{10}$/
    return regex.test(code)
  }

  // アノテーションを削除
  const handleDeleteAnnotation = (field: AnnotationFieldType) => {
    const updatedAnnotations = annotations.filter((a) => a.field !== field)
    onAnnotationsChange(updatedAnnotations)
  }

  // 統一医療機関コードの変更ハンドラ
  const handleCodeChange = (value: string) => {
    onUnifiedMedicalCodeChange(value)
    setCodeError('')
  }

  // 現在の処方箋1件分をExcel出力
  const exportToExcel = () => {
    if (!prescription) return

    if (!validateUnifiedMedicalCode(unifiedMedicalCode)) {
      setCodeError('統一医療機関コードは10桁の半角数字で入力してください')
      return
    }

    const headers = [
      'request_id',
      '統一医療機関コード',
      'is_kigou_bangou_2_lines',
      'hokensha_num_origin_point_height_ratio',
      'hokensha_num_origin_point_width_ratio',
      'hokensha_num_range_height_ratio',
      'hokensha_num_range_width_ratio',
      'kigou_bangou_origin_point_height_ratio',
      'kigou_bangou_origin_point_width_ratio',
      'kigou_bangou_range_height_ratio',
      'kigou_bangou_range_width_ratio',
      'kouhi_1_origin_point_height_ratio',
      'kouhi_1_origin_point_width_ratio',
      'kouhi_1_range_height_ratio',
      'kouhi_1_range_width_ratio',
      'kouhi_2_origin_point_height_ratio',
      'kouhi_2_origin_point_width_ratio',
      'kouhi_2_range_height_ratio',
      'kouhi_2_range_width_ratio',
      'edaban_origin_point_height_ratio',
      'edaban_origin_point_width_ratio',
      'edaban_range_height_ratio',
      'edaban_range_width_ratio',
    ]

    const row: (string | number)[] = [
      prescription.ocr_request_event_id || '',
      unifiedMedicalCode,
      isKigouTwoColumn ? 1 : 0,
    ]

    const fields: AnnotationFieldType[] = [
      '保険者番号',
      '記号番号',
      '公費負担番号',
      '公費受給番号',
      '枝番',
    ]
    fields.forEach((field) => {
      const annotation = annotations.find((a) => a.field === field)
      if (annotation) {
        row.push(
          annotation.origin_point_height_ratio,
          annotation.origin_point_width_ratio,
          annotation.range_height_ratio,
          annotation.range_width_ratio
        )
      } else {
        row.push('', '', '', '')
      }
    })

    const data = [headers, row]
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(data)
    XLSX.utils.book_append_sheet(wb, ws, '保険番号座標')
    XLSX.writeFile(wb, 'アノテーション結果_保険番号座標.xlsx')

    setCodeError('')
  }

  return (
    <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        番号系アノテーション設定
      </Typography>
      <TextField
        label="統一医療機関コード"
        value={unifiedMedicalCode}
        onChange={(e) => handleCodeChange(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
        error={!!codeError}
        helperText={codeError || '10桁の統一医療機関コードを入力してください'}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={isKigouTwoColumn}
            onChange={(e) => onIsKigouTwoColumnChange(e.target.checked)}
            sx={{
              color: 'primary.main',
              '&.Mui-checked': {
                color: 'primary.main',
              },
            }}
          />
        }
        label="記号番号が二段組である"
        sx={{ mb: 2 }}
      />
      <ToggleButtonGroup
        value={selectedField}
        exclusive
        onChange={(_, value) => value && onFieldChange(value)}
        fullWidth
        sx={{ mb: 2 }}
      >
        {Object.keys(fieldColors).map((field) => (
          <ToggleButton
            key={field}
            value={field}
            size="small"
            sx={[
              {
                '&.Mui-selected': {
                  bgcolor: fieldColors[field as AnnotationFieldType],
                },
              },
              selectedField === field
                ? {
                    bgcolor: fieldColors[field as AnnotationFieldType],
                  }
                : {
                    bgcolor: null,
                  },
            ]}
          >
            {field}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Divider sx={{ my: 2 }} />
      {/* アノテーション済みリスト */}
      <Typography variant="subtitle1" gutterBottom>
        アノテーション済み項目
      </Typography>
      <Stack spacing={1} sx={{ mb: 2 }}>
        {annotations.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            まだアノテーションがありません
          </Typography>
        ) : (
          annotations.map((annotation) => (
            <Box
              key={annotation.field}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                bgcolor: fieldColors[annotation.field],
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">{annotation.field}</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDeleteAnnotation(annotation.field)}
                sx={{
                  borderColor: 'black',
                  color: 'black',
                  '&:hover': {
                    borderColor: 'black',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                削除
              </Button>
            </Box>
          ))
        )}
      </Stack>
      <Divider sx={{ my: 2 }} />
      {/* Excelダウンロード（現在の処方箋1件分） */}
      <Button
        variant="contained"
        fullWidth
        startIcon={<GetAppIcon />}
        onClick={exportToExcel}
        disabled={!prescription || annotations.length === 0}
      >
        Excelダウンロード
      </Button>
    </Paper>
  )
}
